import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, ProgressBar, Button, Spinner, Alert } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Line, Doughnut } from 'react-chartjs-2';
import { Globe } from 'react-feather';
import API from '../utils/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
    if (hasRole(['admin'])) {
      loadSystemHealth();
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await API.get('/dashboard/stats');
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await API.get('/dashboard/system-health');
      setSystemHealth(response.data);
    } catch (err) {
      console.error('System health load error:', err);
    }
  };

  if (loading) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  if (!stats) return null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
  };

  // Generate chart data from real API data
  const generateOrderChartData = () => {
    if (!stats.orders.trend_data) return { labels: [], datasets: [] };

    const labels = stats.orders.trend_data.map(item => {
      const date = new Date(item.date);
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    });

    const data = stats.orders.trend_data.map(item => item.count);

    return {
      labels,
      datasets: [
        {
          label: 'Orders',
          data,
          borderColor: '#58a6ff',
          backgroundColor: 'rgba(88, 166, 255, 0.1)',
          tension: 0.4,
        },
      ],
    };
  };

  const generateStatusChartData = () => {
    if (!stats.orders.by_state) return { labels: [], datasets: [] };

    const completed = stats.orders.by_state.CATCHED_COMPLETED || 0;
    const failed = stats.orders.by_state.FAILED || 0;
    const active = stats.orders.active_orders || 0;

    return {
      labels: ['Success', 'Failed', 'Active'],
      datasets: [
        {
          data: [completed, failed, active],
          backgroundColor: ['#56d364', '#f85149', '#e3b341'],
          borderWidth: 0,
        },
      ],
    };
  };

  const orderChartData = generateOrderChartData();
  const statusChartData = generateStatusChartData();

  const getStatusBadge = (status) => {
    const variants = {
      success: 'success',
      pending: 'warning',
      failed: 'danger',
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getRowStyle = (orderState) => {
    const styles = {
      'CATCHED_COMPLETED': { backgroundColor: 'rgba(86, 211, 100, 0.1)' }, // green tint
      'FAILED': { backgroundColor: 'rgba(248, 81, 73, 0.1)' }, // red tint
      'CANCELLED': { backgroundColor: 'rgba(108, 117, 125, 0.1)' }, // gray tint
      'NEW': { backgroundColor: 'rgba(227, 179, 65, 0.1)' }, // yellow tint
      'PROCESSING': { backgroundColor: 'rgba(88, 166, 255, 0.1)' }, // blue tint
      'READY_FOR_CATCH': { backgroundColor: 'rgba(88, 166, 255, 0.15)' }, // slightly stronger blue
    };
    return styles[orderState] || {};
  };

  return (
    <Container fluid>
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <i className="fas fa-tachometer-alt page-heading-icon" />
            Dashboard
          </h2>
          <div className="text-muted">
            Welcome back, {stats.user_info?.first_name || stats.user_info?.username}
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={6} lg={3} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0" style={{ paddingRight: '10px' }}>
                  <i className="fas fa-globe fa-2x text-primary"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted text-xs text-uppercase mb-1">Total Domains</h6>
                  <h5 className="mb-0">{stats.domains?.total_domains?.toLocaleString() || 0}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0" style={{ paddingRight: '10px' }}>
                  <i className="fas fa-calendar-day fa-2x text-warning"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted text-xs text-uppercase mb-1">Releasing Today</h6>
                  <h5 className="mb-0">{(stats.domains?.releasing_today || 0).toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0" style={{ paddingRight: '10px' }}>
                  <i className="fas fa-shopping-cart fa-2x text-success"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted text-xs text-uppercase mb-1">Active Orders</h6>
                  <h5 className="mb-0">{(stats.orders?.active_orders || 0).toLocaleString()}</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} lg={3} className="mb-3">
          <Card>
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0" style={{ paddingRight: '10px' }}>
                  <i className="fas fa-chart-line fa-2x text-info"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="text-muted text-xs text-uppercase mb-1">Success Rate</h6>
                  <h5 className="mb-0">{stats.orders?.success_rate || 0}%</h5>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col lg={8} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0">Order Trends</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '250px' }}>
                <Line data={orderChartData} options={chartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0">Order Status</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '250px' }}>
                <Doughnut data={statusChartData} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Orders */}
      <Row>
        <Col lg={8} className="mb-3">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Recent Orders</h6>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate('/orders')}
              >
                View All
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {stats.recent_activity?.recent_orders?.length > 0 ? (
                <Table hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Price</th>
                      <th>Drop Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent_activity.recent_orders.map((order) => (
                      <tr key={order.id} style={getRowStyle(order.state)}>
                        <td className="fw-bold">{order.domain}</td>
                        <td>
                          <Badge bg={
                            order.state === 'CATCHED_COMPLETED' ? 'success' :
                            order.state === 'FAILED' ? 'danger' :
                            order.state === 'CANCELLED' ? 'secondary' :
                            'warning'
                          }>
                            {order.state.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td>${order.price?.toFixed(2) || '0.00'}</td>
                        <td className="text-muted text-small">
                          {order.drop_date ? new Date(order.drop_date).toLocaleDateString() : '-'}
                        </td>
                        <td>
                          <Button size="sm" variant="link" className="p-0">
                            <i className="fas fa-eye"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted">No recent orders</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4} className="mb-3">
          <Card>
            <Card.Header>
              <h6 className="mb-0">System Health</h6>
            </Card.Header>
            <Card.Body>
              {hasRole(['admin']) && systemHealth ? (
                <>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-small">Database</span>
                      <span className={`text-small ${systemHealth.database?.status === 'healthy' ? 'text-success' : 'text-danger'}`}>
                        {systemHealth.database?.status || 'Unknown'}
                      </span>
                    </div>
                    <ProgressBar
                      now={systemHealth.database?.status === 'healthy' ? 100 : 0}
                      variant={systemHealth.database?.status === 'healthy' ? 'success' : 'danger'}
                      style={{ height: '5px' }}
                    />
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-small">ClickHouse</span>
                      <span className={`text-small ${systemHealth.clickhouse?.status === 'healthy' ? 'text-success' : 'text-danger'}`}>
                        {systemHealth.clickhouse?.status || 'Unknown'}
                      </span>
                    </div>
                    <ProgressBar
                      now={systemHealth.clickhouse?.status === 'healthy' ? 100 : 0}
                      variant={systemHealth.clickhouse?.status === 'healthy' ? 'success' : 'danger'}
                      style={{ height: '5px' }}
                    />
                  </div>

                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-small">Records</span>
                      <span className="text-small text-info">
                        {systemHealth.clickhouse?.total_records?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div className="d-flex justify-content-between mb-1">
                      <span className="text-small">Queue Size</span>
                      <span className="text-small text-primary">
                        {systemHealth.queue?.size || 0}
                      </span>
                    </div>
                    <ProgressBar
                      now={Math.min((systemHealth.queue?.size || 0) / 10, 100)}
                      variant="primary"
                      style={{ height: '5px' }}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center py-3">
                  <p className="text-muted text-small">
                    {hasRole(['admin']) ? 'Loading system health...' : 'System health data available for admins only'}
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Admin specific stats */}
        {hasRole(['admin']) && stats.admin && (
          <Col lg={12} className="mb-3">
            <Card>
              <Card.Header>
                <h6 className="mb-0">Admin Statistics</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-primary">{(stats.admin.total_users || 0).toLocaleString()}</h4>
                      <small className="text-muted">Total Users</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-success">{(stats.admin.active_users || 0).toLocaleString()}</h4>
                      <small className="text-muted">Active Users</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-info">{(stats.admin.total_managers || 0).toLocaleString()}</h4>
                      <small className="text-muted">Managers</small>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h4 className="text-warning">${stats.orders?.total_amount?.toFixed(2) || '0.00'}</h4>
                      <small className="text-muted">Total Revenue</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </Container>
  );
};

export default Dashboard;